import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Descriptions,
  Dropdown,
  Input,
  Modal,
  Progress,
  Row,
  Space,
  Table,
  Tag,
  Typography,
  message,
  Tooltip,
} from 'antd';
import {
  DownloadOutlined,
  ExclamationCircleOutlined,
  LineChartOutlined,
  ReloadOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import dayjs, { Dayjs } from 'dayjs';
import { Link } from 'react-router-dom';
import { websiteApi, GaTrafficSyncJobStatus } from '../services/api';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

type RangeValue = [Dayjs | null, Dayjs | null] | null;

const formatNum = (value?: number) => (value ?? 0).toLocaleString();

type TableSortOrder = 'ascend' | 'descend' | null;

const toTableSortOrder = (
  activeField: string,
  sortBy: string,
  sortOrder: 'asc' | 'desc',
): TableSortOrder => {
  if (sortBy !== activeField) {
    return null;
  }
  return sortOrder === 'asc' ? 'ascend' : 'descend';
};

const GaTrafficPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [range, setRange] = useState<RangeValue>([
    dayjs().subtract(6, 'day'),
    dayjs(),
  ]);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [sortBy, setSortBy] = useState<'users' | 'sessions' | 'pageviews' | 'domain'>('users');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [syncingId, setSyncingId] = useState<string | null>(null);
  const [syncJobId, setSyncJobId] = useState<string | null>(null);
  const completedNotifiedRef = useRef<string | null>(null);
  /** Only toast when we started/resumed a running job — not on F5 of an old completed job */
  const watchCompletionRef = useRef(false);

  const from = range?.[0]?.format('YYYY-MM-DD') || dayjs().subtract(6, 'day').format('YYYY-MM-DD');
  const to = range?.[1]?.format('YYYY-MM-DD') || dayjs().format('YYYY-MM-DD');

  const { data, isLoading, isFetching } = useQuery(
    ['ga-traffic-list', from, to, page, pageSize, search, sortBy, sortOrder],
    () =>
      websiteApi.listGaTraffic({
        from,
        to,
        page,
        limit: pageSize,
        search: search || undefined,
        sortBy,
        sortOrder,
      }),
    { keepPreviousData: true },
  );

  const { data: latestJob } = useQuery(
    ['ga-traffic-sync-latest'],
    () => websiteApi.getLatestGaTrafficSyncJob(),
    {
      onSuccess: (job) => {
        if (!job) {
          return;
        }
        if (job.status === 'pending' || job.status === 'processing') {
          setSyncJobId(job.jobId);
          watchCompletionRef.current = true;
          return;
        }
        // Already finished job from a previous visit — silence toast on F5
        completedNotifiedRef.current = job.jobId;
      },
    },
  );

  const { data: syncJob } = useQuery(
    ['ga-traffic-sync-job', syncJobId],
    () => websiteApi.getGaTrafficSyncJob(syncJobId!),
    {
      enabled: !!syncJobId,
      refetchInterval: (data) =>
        data?.status === 'pending' || data?.status === 'processing' ? 2000 : false,
    },
  );

  const runningLatest =
    latestJob && (latestJob.status === 'pending' || latestJob.status === 'processing')
      ? latestJob
      : null;

  const activeJob: GaTrafficSyncJobStatus | null | undefined =
    syncJob || runningLatest || (watchCompletionRef.current && syncJobId ? syncJob : null);

  const jobRunning =
    activeJob?.status === 'pending' || activeJob?.status === 'processing';

  useEffect(() => {
    if (!activeJob || !watchCompletionRef.current) {
      return;
    }

    if (
      (activeJob.status === 'completed' || activeJob.status === 'failed') &&
      completedNotifiedRef.current !== activeJob.jobId
    ) {
      completedNotifiedRef.current = activeJob.jobId;
      watchCompletionRef.current = false;
      if (activeJob.status === 'completed') {
        message.success(
          `Sync job done: ${activeJob.success}/${activeJob.total} success` +
            (activeJob.failed ? `, ${activeJob.failed} failed` : ''),
        );
        queryClient.invalidateQueries(['ga-traffic-list']);
      } else {
        message.error(activeJob.errorMessage || 'Sync job failed');
      }
      queryClient.invalidateQueries(['ga-traffic-sync-latest']);
    }
  }, [activeJob, queryClient]);

  const syncOneMutation = useMutation(
    (websiteId: string) => websiteApi.syncGaTraffic(websiteId),
    {
      onMutate: (websiteId) => setSyncingId(websiteId),
      onSuccess: () => {
        message.success('GA traffic synced');
        queryClient.invalidateQueries(['ga-traffic-list']);
      },
      onError: (error: any) => {
        message.error(error.response?.data?.message || 'Failed to sync GA traffic');
      },
      onSettled: () => setSyncingId(null),
    },
  );

  const syncAllMutation = useMutation(() => websiteApi.syncAllGaTraffic(), {
    onSuccess: (result) => {
      completedNotifiedRef.current = null;
      watchCompletionRef.current = true;
      setSyncJobId(result.jobId);
      message.info('Sync all job started — running in background');
      queryClient.invalidateQueries(['ga-traffic-sync-latest']);
      queryClient.invalidateQueries(['ga-traffic-sync-job', result.jobId]);
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to start sync all job');
    },
  });

  const exportMutation = useMutation(
    (format: 'csv' | 'excel') =>
      websiteApi.exportGaTraffic({
        from,
        to,
        search: search || undefined,
        format,
      }),
    {
      onSuccess: (blob, format) => {
        const ext = format === 'csv' ? 'csv' : 'xls';
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `ga-traffic-${from}_to_${to}.${ext}`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        message.success(`Exported ${ext.toUpperCase()} file`);
      },
      onError: (error: any) => {
        message.error(error.response?.data?.message || 'Failed to export GA traffic');
      },
    },
  );

  const columns = useMemo(
    () => [
      {
        title: 'Domain',
        dataIndex: 'domain',
        key: 'domain',
        sorter: true as const,
        sortOrder: toTableSortOrder('domain', sortBy, sortOrder),
        render: (domain: string, record: any) => (
          <Space>
            <Link to={`/websites/${record.websiteId}`}>{domain}</Link>
            {record.sync_error ? <Tag color="red">Error</Tag> : null}
          </Space>
        ),
      },
      {
        title: 'GA Key',
        dataIndex: 'ga_tag_key',
        key: 'ga_tag_key',
        render: (value?: string) => (value ? <Tag color="blue">{value}</Tag> : '—'),
      },
      {
        title: 'Users',
        dataIndex: 'users',
        key: 'users',
        align: 'right' as const,
        sorter: true as const,
        sortOrder: toTableSortOrder('users', sortBy, sortOrder),
        render: formatNum,
      },
      {
        title: 'Sessions',
        dataIndex: 'sessions',
        key: 'sessions',
        align: 'right' as const,
        sorter: true as const,
        sortOrder: toTableSortOrder('sessions', sortBy, sortOrder),
        render: formatNum,
      },
      {
        title: 'Pageviews',
        dataIndex: 'pageviews',
        key: 'pageviews',
        align: 'right' as const,
        sorter: true as const,
        sortOrder: toTableSortOrder('pageviews', sortBy, sortOrder),
        render: formatNum,
      },
      {
        title: 'Last synced',
        dataIndex: 'last_synced_at',
        key: 'last_synced_at',
        render: (value?: string | null) =>
          value ? dayjs(value).format('YYYY-MM-DD HH:mm') : 'Never',
      },
      {
        title: 'Action',
        key: 'action',
        width: 100,
        render: (_: unknown, record: any) => (
          <Tooltip title={record.sync_error || 'Sync from GA4'}>
            <Button
              size="small"
              icon={<ReloadOutlined />}
              loading={syncingId === record.websiteId}
              disabled={jobRunning || !record.ga_tag_key}
              onClick={() => syncOneMutation.mutate(record.websiteId)}
            >
              Sync
            </Button>
          </Tooltip>
        ),
      },
    ],
    [syncOneMutation, syncingId, jobRunning, sortBy, sortOrder],
  );

  const rangePresets: { label: string; value: [Dayjs, Dayjs] }[] = [
    { label: 'Today', value: [dayjs(), dayjs()] },
    { label: 'Last 3 days', value: [dayjs().subtract(2, 'day'), dayjs()] },
    { label: 'Last 7 days', value: [dayjs().subtract(6, 'day'), dayjs()] },
    { label: 'Last 30 days', value: [dayjs().subtract(29, 'day'), dayjs()] },
  ];

  const progressPercent =
    activeJob && activeJob.total > 0
      ? Math.round((activeJob.processed / activeJob.total) * 100)
      : jobRunning
        ? 0
        : activeJob?.status === 'completed'
          ? 100
          : 0;

  const confirmSyncAll = () => {
    Modal.confirm({
      title: 'Sync all GA traffic?',
      icon: <ExclamationCircleOutlined />,
      content:
        'This will start a background sync for every website with a GA key. Continue?',
      okText: 'Yes',
      cancelText: 'No',
      onOk: () => {
        syncAllMutation.mutate();
      },
    });
  };

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
        <Space>
          <LineChartOutlined style={{ fontSize: 22 }} />
          <Title level={2} style={{ margin: 0 }}>
            GA Traffic
          </Title>
        </Space>
        <Space>
          <Dropdown
            menu={{
              items: [
                { key: 'csv', label: 'Export CSV', onClick: () => exportMutation.mutate('csv') },
                { key: 'excel', label: 'Export Excel', onClick: () => exportMutation.mutate('excel') },
              ],
            }}
          >
            <Button icon={<DownloadOutlined />} loading={exportMutation.isLoading}>
              Export
            </Button>
          </Dropdown>
          <Button
            type="primary"
            icon={<ReloadOutlined />}
            loading={syncAllMutation.isLoading || jobRunning}
            disabled={jobRunning}
            onClick={confirmSyncAll}
          >
            {jobRunning ? 'Syncing…' : 'Sync all'}
          </Button>
        </Space>
      </div>

      {activeJob && (jobRunning || activeJob.status === 'completed' || activeJob.status === 'failed') && (
        <Card size="small" style={{ marginBottom: 16 }}>
          <Space direction="vertical" style={{ width: '100%' }} size="small">
            <Space wrap>
              <Text strong>Sync all job</Text>
              <Tag
                color={
                  activeJob.status === 'completed'
                    ? 'green'
                    : activeJob.status === 'failed'
                      ? 'red'
                      : 'blue'
                }
              >
                {activeJob.status.toUpperCase()}
              </Tag>
              <Text type="secondary" code>
                {activeJob.jobId}
              </Text>
            </Space>
            <Progress
              percent={progressPercent}
              status={
                activeJob.status === 'failed'
                  ? 'exception'
                  : activeJob.status === 'completed'
                    ? 'success'
                    : 'active'
              }
            />
            <Text type="secondary">
              {activeJob.processed}/{activeJob.total || '?'} processed · success {activeJob.success} ·
              failed {activeJob.failed}
              {activeJob.currentDomain ? ` · current: ${activeJob.currentDomain}` : ''}
            </Text>
            {activeJob.errorMessage ? (
              <Alert type="error" showIcon message={activeJob.errorMessage} />
            ) : null}
            {activeJob.errors?.length ? (
              <Alert
                type="warning"
                showIcon
                message={`${activeJob.errors.length} recent error(s)`}
                description={
                  <ul style={{ margin: 0, paddingLeft: 18 }}>
                    {activeJob.errors.slice(-5).map((err) => (
                      <li key={`${err.domain}-${err.error}`}>
                        <Text code>{err.domain}</Text>: {err.error}
                      </li>
                    ))}
                  </ul>
                }
              />
            ) : null}
          </Space>
        </Card>
      )}

      <Card style={{ marginBottom: 16 }}>
        <Space wrap size="middle">
          <div>
            <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>
              Date range
            </Text>
            <RangePicker
              value={range}
              presets={rangePresets}
              allowClear={false}
              onChange={(value) => {
                setRange(value);
                setPage(1);
              }}
            />
          </div>
          <div>
            <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>
              Search
            </Text>
            <Input.Search
              placeholder="Domain or GA key"
              allowClear
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onSearch={(value) => {
                setSearch(value.trim());
                setPage(1);
              }}
              enterButton={<SearchOutlined />}
              style={{ width: 260 }}
            />
          </div>
          <Text type="secondary">
            Showing {from} → {to}
            {isFetching && !isLoading ? ' · updating…' : ''}
          </Text>
        </Space>
      </Card>

      <Card>
        <Table
          rowKey="websiteId"
          loading={isLoading}
          columns={columns}
          dataSource={data?.data || []}
          onChange={(pagination, _filters, sorter, extra) => {
            if (extra.action === 'paginate') {
              setPage(pagination.current || 1);
              setPageSize(pagination.pageSize || 50);
              return;
            }

            if (extra.action !== 'sort') {
              return;
            }

            const active = Array.isArray(sorter) ? sorter[0] : sorter;
            const field = String(active?.field || active?.columnKey || '');
            if (
              field !== 'users' &&
              field !== 'sessions' &&
              field !== 'pageviews' &&
              field !== 'domain'
            ) {
              return;
            }

            setPage(1);
            if (active?.order === 'ascend') {
              setSortBy(field);
              setSortOrder('asc');
            } else if (active?.order === 'descend') {
              setSortBy(field);
              setSortOrder('desc');
            } else {
              setSortBy('users');
              setSortOrder('desc');
            }
          }}
          expandable={{
            expandedRowRender: (record) => {
              const periods = record.periods || {
                today: { users: 0, sessions: 0, pageviews: 0 },
                last_3d: { users: 0, sessions: 0, pageviews: 0 },
                last_7d: { users: 0, sessions: 0, pageviews: 0 },
                last_30d: { users: 0, sessions: 0, pageviews: 0 },
              };
              const periodCards = [
                { key: 'today', title: 'Today', data: periods.today },
                { key: 'last_3d', title: 'Last 3 days', data: periods.last_3d },
                { key: 'last_7d', title: 'Last 7 days', data: periods.last_7d },
                { key: 'last_30d', title: 'Last 30 days', data: periods.last_30d },
              ];

              return (
                <div>
                  {record.sync_error ? (
                    <Text type="danger" style={{ display: 'block', marginBottom: 8 }}>
                      Sync error: {record.sync_error}
                    </Text>
                  ) : null}

                  <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
                    {periodCards.map((period) => (
                      <Col xs={24} sm={12} lg={6} key={period.key}>
                        <Card size="small" title={period.title} type="inner">
                          <Descriptions column={1} size="small">
                            <Descriptions.Item label="Users">
                              {formatNum(period.data?.users)}
                            </Descriptions.Item>
                            <Descriptions.Item label="Sessions">
                              {formatNum(period.data?.sessions)}
                            </Descriptions.Item>
                            <Descriptions.Item label="Pageviews">
                              {formatNum(period.data?.pageviews)}
                            </Descriptions.Item>
                          </Descriptions>
                        </Card>
                      </Col>
                    ))}
                  </Row>

                  <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                    Daily breakdown for selected range
                  </Text>
                  <Table
                    size="small"
                    pagination={false}
                    rowKey="date"
                    dataSource={record.daily || []}
                    locale={{ emptyText: 'No daily data in this range — click Sync' }}
                    columns={[
                      { title: 'Date', dataIndex: 'date', key: 'date' },
                      {
                        title: 'Users',
                        dataIndex: 'users',
                        key: 'users',
                        align: 'right' as const,
                        render: formatNum,
                      },
                      {
                        title: 'Sessions',
                        dataIndex: 'sessions',
                        key: 'sessions',
                        align: 'right' as const,
                        render: formatNum,
                      },
                      {
                        title: 'Pageviews',
                        dataIndex: 'pageviews',
                        key: 'pageviews',
                        align: 'right' as const,
                        render: formatNum,
                      },
                    ]}
                  />
                </div>
              );
            },
          }}
          pagination={{
            current: page,
            pageSize,
            total: data?.total || 0,
            showSizeChanger: true,
            showTotal: (total) => `${total} active websites with GA key`,
          }}
        />
      </Card>
    </div>
  );
};

export default GaTrafficPage;
